// Utilities
import { defineStore } from 'pinia'
import { type AuthError, createUserWithEmailAndPassword, getAuth, signInWithEmailAndPassword, type User as FirebaseUser, sendPasswordResetEmail, sendEmailVerification, reload, setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import router from '@/router/index.js';
import { useFirestore } from 'vuefire';
import { doc, DocumentSnapshot, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, addDoc, orderBy, limit, deleteField, arrayUnion, arrayRemove, type UpdateData, type DocumentData } from 'firebase/firestore';
import { User as AppUser, NotificationPreferences, type PreferredLanguage } from '@/models/User'
import { Invitation } from '@/models/Billing';
import { useAnalyticsStore } from '@/stores/analytics'
import { useAppStore } from '@/stores/app'
// import Clarity from '@microsoft/clarity';
import { logger } from '@/utils/logger'
import { safeInAppPath } from '@/utils/safeRedirect'
import i18n, { setInterfaceLanguage } from '@/plugins/i18n'
import { isInterfaceLanguage, type InterfaceLanguage } from '@/utils/languages'
import { writeWelcomeLetterHiddenHere } from '@/utils/welcomeLetter'
import { readSessionVerdict, rememberMeTimestampKey, SESSION_CHECK_INTERVAL_MS, sessionStartKey } from '@/utils/sessionExpiry'
import { startActivityTracker, stopActivityTracker, trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import {
  clearSessionSkip,
  ONBOARDING_QUESTIONS,
  ONBOARDING_STEPS,
  ONBOARDING_TEXT_MAX,
  parseOnboardingAnswers,
  parseOnboardingSkipped,
  PERSONAL_NAME_MAX,
  readSessionSkip,
  writeSessionSkip,
  type OnboardingAnswers,
  type OnboardingQuestion,
  type OnboardingStep,
} from '@/types/personOnboarding'

// export class FirestoreUser {
//   public is_super_admin: boolean;
// }

// MYAC-1626 — the open tab's expiry watch: one timer and one listener per page.
let sessionWatchTimer: ReturnType<typeof setInterval> | null = null
let sessionWatchListener: (() => void) | null = null

/**
 * MYAC-1348 — how the support chat signs this user in: their email and an HMAC of
 * it, written by the backend on their own user document. The hash is a bearer
 * credential for their support conversations, so it is only ever read, never
 * logged or stored anywhere else.
 */
export interface SupportChatIdentity {
  email: string
  email_hash: string
}

export const useUsersStore = defineStore('users', {
  state: () => ({
    user: null as FirebaseUser | null,
    firestoreUserSnapshot: null as DocumentSnapshot | null,
    loginError: null as AuthError | null,
    registerError: null as AuthError | null,
    passwordResetError: null as AuthError | null,
    loading: false,
    usersById: {} as Record<string, AppUser | null>,
    invitations: [] as Invitation[],
    isFreshLogin: false, // Track if this is a fresh login/register to avoid signing out session persistence users
    // Highest terms-of-use sequence this user has accepted (MYAC-824), read from
    // the users/{uid}/consents audit subcollection. null = never accepted / not loaded.
    latestConsentSequence: null as number | null,
    // MYAC-1337 — the account that skipped the onboarding questions in THIS page's
    // life (sessionStorage carries it across reloads when it is available).
    onboardingSkippedUid: null as string | null,
  }),
  getters: {
    uid: (state) => {
      if (!state.user?.uid)
        throw new Error("User not set")
      return state.user?.uid
    },
    isSuperAdmin: (state) => state.firestoreUserSnapshot?.get('is_super_admin'),
    userById: (state) => (id: string): AppUser | null | undefined => state.usersById[id],
    isLoggedIn: (state) => state.user !== null,
    /** Null until the backend has written one — the chat then stays unavailable. */
    supportChatIdentity: (state): SupportChatIdentity | null => {
      const raw = state.firestoreUserSnapshot?.exists() ? state.firestoreUserSnapshot.get('support_chat') : null
      if (typeof raw?.email !== 'string' || typeof raw?.email_hash !== 'string') return null
      if (!raw.email || !raw.email_hash) return null
      return { email: raw.email, email_hash: raw.email_hash }
    },
    /**
     * MYAC-1522 — the name the person gave (« Faisons connaissance », profile);
     * empty strings when they have not.
     */
    personalName: (state): { first_name: string, last_name: string } => {
      const snap = state.firestoreUserSnapshot?.exists() ? state.firestoreUserSnapshot : null
      const read = (key: string) => {
        const v = snap?.get(key)
        return typeof v === 'string' ? v.trim() : ''
      }
      return { first_name: read('first_name'), last_name: read('last_name') }
    },
    /**
     * The first name to greet the person by, or null — never a word of the display
     * name, which may be the last name alone (MYAC-1540, MYAC-1562).
     */
    firstName(): string | null {
      return this.personalName.first_name || null
    },
    /**
     * The account's display name, or null — never a stand-in such as the email.
     * Read from display_name only, the field the backend records (the regulation
     * report's signer) and copies onto member records: a preview shows what will
     * be recorded (PR 907 review). Giving a name writes it there (savePersonalName).
     * A stored null is a name cleared on the profile: it stays null, never the Auth
     * name, which only stands in while the field was never written (MYAC-1562).
     */
    displayName: (state): string | null => {
      const stored = state.firestoreUserSnapshot?.exists() ? state.firestoreUserSnapshot.get('display_name') : undefined
      const name = stored === undefined ? state.user?.displayName : stored
      return (typeof name === 'string' ? name.trim() : '') || null
    },
    notificationPreferences: (state): NotificationPreferences | null => {
      if (!state.firestoreUserSnapshot?.exists()) return null
      const data = state.firestoreUserSnapshot.data()
      const prefs = data?.notification_preferences
      if (!prefs) return new NotificationPreferences()
      // Return a new NotificationPreferences instance with the data
      const result = new NotificationPreferences()
      result.assessment_completed_projects = prefs.assessment_completed_projects ?? []
      result.regulation_watch_projects = prefs.regulation_watch_projects ?? []
      result.preferred_language = prefs.preferred_language ?? 'en'
      result.next_weekly_send_date = prefs.next_weekly_send_date?.toDate?.() ?? prefs.next_weekly_send_date ?? null
      result.notification_setup_completed = prefs.notification_setup_completed ?? false
      result.weekly_summary_disabled = prefs.weekly_summary_disabled ?? false
      result.interface_language = isInterfaceLanguage(prefs.interface_language) ? prefs.interface_language : null
      result.welcome_letter_hidden = prefs.welcome_letter_hidden === true
      result.watch_report_asked = prefs.watch_report_asked === true
      return result
    },
    /**
     * The language this user actually saved, or null when the document has none —
     * unlike notificationPreferences, which reads an absent value as 'en' and so
     * cannot tell "chose English" from "never chose" (MYAC-1323).
     */
    storedPreferredLanguage: (state): PreferredLanguage | null => {
      if (!state.firestoreUserSnapshot?.exists()) return null
      const stored = state.firestoreUserSnapshot.get('notification_preferences.preferred_language')
      return stored === 'fr' || stored === 'en' ? stored : null
    },
    /** MYAC-1337 — what the person told the Pilotage chat; null = never answered, or erased. */
    onboardingAnswers: (state): OnboardingAnswers | null => {
      if (!state.firestoreUserSnapshot?.exists()) return null
      return parseOnboardingAnswers(state.firestoreUserSnapshot.get('onboarding_answers'))
    },
    /** MYAC-1522 — the « Faisons connaissance » steps skipped for good. */
    onboardingSkipped: (state): OnboardingStep[] => {
      if (!state.firestoreUserSnapshot?.exists()) return []
      return parseOnboardingSkipped(state.firestoreUserSnapshot.get('onboarding_skipped'))
    },
    /**
     * MYAC-1522 — the steps still to ask: neither given nor skipped for good. An
     * account that answered the three questions long ago is asked its name alone;
     * a new one is asked everything.
     */
    missingOnboardingSteps(state): OnboardingStep[] {
      if (!state.firestoreUserSnapshot?.exists()) return []
      const skipped = this.onboardingSkipped
      const answers = this.onboardingAnswers
      return ONBOARDING_STEPS.filter(step => !skipped.includes(step)
        && (step === 'name' ? !this.personalName.first_name : !answers?.[step]))
    },
    /**
     * MYAC-1337 / MYAC-1522 — ask « Faisons connaissance »? Only once the user
     * document has been read (an unread document is not "never answered"), only
     * while a step is missing, and not again in a session where the person put it
     * off (« Plus tard »).
     */
    shouldAskOnboarding(state): boolean {
      const uid = state.user?.uid
      if (!uid || !state.firestoreUserSnapshot?.exists()) return false
      if (this.missingOnboardingSteps.length === 0) return false
      return state.onboardingSkippedUid !== uid && !readSessionSkip(uid)
    },
    isNotificationPreferencesSetup: (state): boolean => {
      if (!state.firestoreUserSnapshot?.exists()) return true // no doc = no popup
      const data = state.firestoreUserSnapshot.data()
      const prefs = data?.notification_preferences
      if (!prefs) return false
      // Deliberately NOT next_weekly_send_date: the weekly-recap cron now sets
      // that for every user (MYAC-1199), so it no longer says anything about
      // whether this person has seen the setup dialog.
      return prefs.notification_setup_completed === true
    },
  },
  actions: {
    async setUser(user: FirebaseUser) {
      try {
        this.user = user;
        if (this.user) {
          // Check session expiration before proceeding
          await this.checkSessionExpiration()
          this.startSessionExpiryWatch()
          await this.getFirestoreUser(user.uid);
          await this.loadLatestConsent()
        }
        try {
          const analytics = useAnalyticsStore()
          analytics.setUserId(user ? user.uid : null)
        } catch { }
        try {
          if (user) startActivityTracker()
          else stopActivityTracker()
        } catch { }
      } catch (error) {
        logger.error("Error setting user", error);
      }
    },
    async checkSessionExpiration() {
      if (!this.user?.uid) {
        return
      }

      try {
        const verdict = readSessionVerdict(this.user.uid, Date.now())
        // No stored sign-in time is an old local persistence session — unless the
        // login is still writing it (`isFreshLogin`).
        if (verdict === 'expired' || (verdict === 'unmarked' && !this.isFreshLogin)) {
          logger.info(`Session ${verdict}, signing out user`)
          // Before the first await, so a focus event firing meanwhile finds no listener.
          this.stopSessionExpiryWatch()
          await this.logout()
        }
      } catch (error) {
        logger.error('Error checking session expiration', error)
        // Don't block user access if check fails
      } finally {
        // Reset flag after check
        this.isFreshLogin = false
      }
    },
    /**
     * MYAC-1626 — re-run the expiry check while the tab stays open: on a timer, and
     * when the tab is shown again (a backgrounded tab's timer is throttled, so
     * coming back to it after the limit must not wait for the next tick).
     */
    // csm-event: none — session plumbing, not a user gesture
    startSessionExpiryWatch() {
      if (!this.user || sessionWatchTimer !== null) return
      sessionWatchTimer = setInterval(() => { void this.checkSessionExpiration() }, SESSION_CHECK_INTERVAL_MS)
      sessionWatchListener = () => {
        if (document.visibilityState === 'visible') void this.checkSessionExpiration()
      }
      document.addEventListener('visibilitychange', sessionWatchListener)
      window.addEventListener('focus', sessionWatchListener)
    },
    // csm-event: none — session plumbing, not a user gesture
    stopSessionExpiryWatch() {
      if (sessionWatchTimer !== null) clearInterval(sessionWatchTimer)
      sessionWatchTimer = null
      if (sessionWatchListener) {
        document.removeEventListener('visibilitychange', sessionWatchListener)
        window.removeEventListener('focus', sessionWatchListener)
      }
      sessionWatchListener = null
    },
    async getFirestoreUser(id: string) {
      if (!id) {
        this.firestoreUserSnapshot = null
        return
      }
      try {
        const q = doc(useFirestore(), `/users/${id}`);
        await getDoc(q).then((d) => {
          this.firestoreUserSnapshot = d
          // The interface language follows the account, from any browser (MYAC-1522).
          if (d.exists()) {
            const chosen = d.get('notification_preferences.interface_language')
            setInterfaceLanguage(isInterfaceLanguage(chosen) ? chosen : null)
            // The welcome letter's « Ne plus afficher » too, kept on this browser
            // because the next arrival decides before the account has loaded.
            writeWelcomeLetterHiddenHere(id, d.get('notification_preferences.welcome_letter_hidden') === true)
          }
        }).catch((error) => logger.error("Error getting user", error));
      } catch (error) {
        logger.error("Error getting user", error);
      }
    },
    /**
     * Load the highest terms-of-use sequence this user has accepted (MYAC-824),
     * from the users/{uid}/consents audit subcollection. Cached on the store and
     * read by the router re-consent gate.
     */
    async loadLatestConsent() {
      const uid = this.user?.uid
      if (!uid) {
        this.latestConsentSequence = null
        return
      }
      // Only touch the consents subcollection once terms are actually in force.
      // Before activation (no system/public_config.terms pointer, and — until the
      // rules are deployed — no rule for the subcollection) this avoids a
      // guaranteed permission-denied on every session.
      const appStore = useAppStore()
      await appStore.loadPublicConfig()
      if ((appStore.publicConfig?.terms?.current_sequence ?? 0) <= 0) {
        this.latestConsentSequence = null
        return
      }
      try {
        const db = useFirestore()
        const q = query(collection(db, `users/${uid}/consents`), orderBy('sequence', 'desc'), limit(1))
        const snap = await getDocs(q)
        const seq = snap.docs[0]?.data()?.sequence
        this.latestConsentSequence = typeof seq === 'number' ? seq : null
      } catch (error) {
        // Owner-only read; a transient failure must not lock the user out — leave
        // the sequence null and let the gate degrade (see router beforeEach).
        logger.error('Error loading latest consent', error)
        this.latestConsentSequence = null
      }
    },
    /**
     * Durably record that the current user accepted the in-force terms version.
     * Writes an immutable audit doc to users/{uid}/consents. The version, sequence
     * and content_hash come from the *same* terms_versions doc whose body was
     * rendered to the user (MYAC-824) — so we can never record consent for a
     * version whose text they didn't see. No-op when no terms are in force.
     */
    async recordConsent(source: 'registration' | 're_consent', locale: string): Promise<void> {
      const uid = getAuth().currentUser?.uid
      if (!uid) {
        logger.error('recordConsent: no current user')
        return
      }
      const appStore = useAppStore()
      const terms = await appStore.loadInForceTerms()
      const version = terms?.version ?? ''
      const sequence = Math.trunc(terms?.sequence ?? 0)
      if (!terms || !version || sequence <= 0) {
        // No terms in force — nothing to record (backward compatible).
        return
      }
      try {
        const db = useFirestore()
        await addDoc(collection(db, `users/${uid}/consents`), {
          version,
          sequence,
          accepted_at: serverTimestamp(),
          locale,
          content_hash: terms.contentHashFor(locale),
          source,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        })
        await this.loadLatestConsent()
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('terms_consent_recorded', { version, source })
        } catch { }
      } catch (error) {
        logger.error('Error recording consent', error)
        return Promise.reject(error)
      }
    },
    async loadInvitationsForUser() {
      if (!this.user) {
        logger.error('No user found')
        return
      }
      try {
        const db = useFirestore()
        const normalizedEmail = this.user.email?.trim().toLowerCase() || ''
        const q = query(collection(db, 'invitations'), where('email', '==', normalizedEmail), where('status', '==', 'pending'))
        const snap = await getDocs(q)
        logger.debug("snap docs", snap.docs)
        this.invitations = snap.docs.map((d) => Invitation.fromFirebase(d)).filter((x): x is Invitation => x !== null)
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('invitations_loaded', { count: this.invitations.length })
        } catch { }
      } catch (error) {
        logger.error('Error loading invitations', error)
      }
    },
    async acceptInvitation(invitationId: string) {
      try {
        if (!invitationId) {
          logger.error('acceptInvitation: invitationId is required')
          return
        }
        const db = useFirestore()
        const ref = doc(db, 'invitations', invitationId)
        await updateDoc(ref, {
          status: 'accept',
          updated_at: serverTimestamp(),
        })
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('invitation_accept_success')
        } catch { }
      } catch (error) {
        logger.error('Error accepting invitation', error)
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('invitation_accept_error', { message: (error as Error)?.message })
        } catch { }
        return Promise.reject(error)
      }
    },
    /**
     * `next` is where to land AFTER signing out — used by the invitations page
     * so that logging out to switch accounts returns to the invitation instead
     * of dropping its `invitation_id` / `email` at /login (MYAC-1230).
     *
     * Bounded by `safeInAppPath`, the same helper the other auth screens use:
     * `?redirect=` arrives from a URL anyone can hand a user, and this one ends
     * at `window.location.assign`, so an off-site target here is a phishing
     * redirect fired at a freshly signed-out user.
     */
    async logout(next?: string) {
      const auth = getAuth()
      // First, before the markers go: a tick or a focus during the awaits below
      // would read the session as unmarked and start a second logout (MYAC-1626).
      this.stopSessionExpiryWatch()
      // Clear remember me timestamp and session marker on logout (before clearing user)
      const userId = this.user?.uid
      if (userId) {
        try {
          localStorage.removeItem(rememberMeTimestampKey(userId))
          sessionStorage.removeItem(sessionStartKey(userId))
        } catch (error) {
          logger.error('Error clearing auth markers on logout', error)
        }
        try {
          const { clearPersistedTenantId } = await import('./tenants')
          clearPersistedTenantId(userId)
        } catch (error) {
          logger.error('Error clearing persisted tenant id on logout', error)
        }
      }
      // CASA 2.2.1 (MYAC-1191): `auth.signOut()` below is local to this browser —
      // the refresh token this account holds on any OTHER device stays valid and
      // keeps minting ID tokens. `revoke_sessions` (functions-auth) moves the
      // account's `tokens_valid_after` watermark, so those devices die at their
      // next refresh. Best effort by design: a failure here must never trap the
      // user in a session they asked to leave, so it is logged and sign-out
      // continues. Reached by the 30-day expiry path too, which calls this action.
      if (auth.currentUser) {
        try {
          const { getRevokeSessionsFunction } = await import('@/plugins/firestore')
          await getRevokeSessionsFunction()()
        } catch (error) {
          logger.warn('Could not revoke sessions server-side on logout', error)
        }
      }
      await auth.signOut()
      this.user = null
      this.latestConsentSequence = null
      try {
        const analytics = useAnalyticsStore()
        analytics.trackEvent('logout')
        analytics.setUserId(null)
      } catch { }
      stopActivityTracker()
      // Force a full reload to reset all Pinia stores and reactive state
      // Use absolute path to ensure correct base handling
      window.location.assign(safeInAppPath(next, '/login'))
    },
    async login(email: string, password: string, rememberMe: boolean = false) {
      this.loading = true
      const auth = getAuth()

      try {
        // Set persistence before signing in (per Firebase docs)
        if (rememberMe) {
          await setPersistence(auth, browserLocalPersistence)
        } else {
          await setPersistence(auth, browserSessionPersistence)
          // Clear any existing remember me timestamp when not using remember me
          const currentUser = auth.currentUser
          if (currentUser) {
            try {
              localStorage.removeItem(rememberMeTimestampKey(currentUser.uid))
            } catch (error) {
              logger.error('Error clearing remember me timestamp', error)
            }
          }
        }
      } catch (error) {
        logger.error('Failed to set persistence', error)
        // Continue with login attempt even if persistence setting fails
      }

      await signInWithEmailAndPassword(auth, email, password)
        .then(async () => {
          // The return leg of the logout round trip reads the same `?redirect=`,
          // so it is bounded by the same helper. `router.push` cannot leave the
          // origin the way `assign` can, so this is consistency rather than a
          // live hole — but one unsanitised reader in the chain is one too many.
          const route = safeInAppPath(router.currentRoute?.value?.query?.redirect, '/')
          router.push(route);
          this.loading = false

          // Mark as fresh login to avoid expiration check signing out session persistence users
          this.isFreshLogin = true

          if (auth.currentUser) {
            if (rememberMe) {
              // Store login timestamp for remember me (local persistence)
              try {
                const timestamp = Date.now()
                localStorage.setItem(rememberMeTimestampKey(auth.currentUser.uid), timestamp.toString())
                // Clear session marker if it exists
                sessionStorage.removeItem(sessionStartKey(auth.currentUser.uid))
              } catch (error) {
                logger.error('Error storing remember me timestamp', error)
              }
            } else {
              // Store the sign-in time for session persistence (survives a refresh, not the browser closing; 8 hours at most, MYAC-1626)
              try {
                sessionStorage.setItem(sessionStartKey(auth.currentUser.uid), Date.now().toString())
                // A remember-me session that ended without logout() leaves its 30-day time behind.
                localStorage.removeItem(rememberMeTimestampKey(auth.currentUser.uid))
              } catch (error) {
                logger.error('Error storing session persistence marker', error)
              }
            }
          }

          try {
            const analytics = useAnalyticsStore()
            analytics.trackEvent('login_success')
          } catch { }
        })
        .catch((reason) => {
          logger.error('Failed signInWithEmailAndPassword', reason)
          this.loginError = reason
          this.loading = false
          try {
            const analytics = useAnalyticsStore()
            analytics.trackEvent('login_failure', { code: (reason as AuthError)?.code })
          } catch { }
        })
    },
    async register(email: string, password: string, rememberMe: boolean = false) {
      this.loading = true
      const auth = getAuth()

      try {
        // Set persistence before creating user (per Firebase docs)
        if (rememberMe) {
          await setPersistence(auth, browserLocalPersistence)
        } else {
          await setPersistence(auth, browserSessionPersistence)
          // Clear any existing remember me timestamp when not using remember me
          const currentUser = auth.currentUser
          if (currentUser) {
            try {
              localStorage.removeItem(rememberMeTimestampKey(currentUser.uid))
            } catch (error) {
              logger.error('Error clearing remember me timestamp', error)
            }
          }
        }
      } catch (error) {
        logger.error('Failed to set persistence', error)
        // Continue with registration attempt even if persistence setting fails
      }

      try {
        await createUserWithEmailAndPassword(auth, email, password)
        this.loading = false
        this.registerError = null

        // Mark as fresh login to avoid expiration check signing out session persistence users
        this.isFreshLogin = true

        if (auth.currentUser) {
          if (rememberMe) {
            // Store login timestamp for remember me (local persistence)
            try {
              const timestamp = Date.now()
              localStorage.setItem(rememberMeTimestampKey(auth.currentUser.uid), timestamp.toString())
              // Clear session marker if it exists
              sessionStorage.removeItem(sessionStartKey(auth.currentUser.uid))
            } catch (error) {
              logger.error('Error storing remember me timestamp', error)
            }
          } else {
            // Store the sign-in time for session persistence (survives a refresh, not the browser closing; 8 hours at most, MYAC-1626)
            try {
              sessionStorage.setItem(sessionStartKey(auth.currentUser.uid), Date.now().toString())
              // A remember-me session that ended without logout() leaves its 30-day time behind.
              localStorage.removeItem(rememberMeTimestampKey(auth.currentUser.uid))
            } catch (error) {
              logger.error('Error storing session persistence marker', error)
            }
          }
        }

        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('register_success')
        } catch { }
        try {
          // Send verification email right after signup without blocking navigation.
          await this.sendVerificationEmail().catch(() => { })
        } catch { }
      } catch (reason) {
        logger.error("Error while registering", reason);
        this.registerError = reason as AuthError;
        this.loading = false
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('register_failure', { code: (reason as AuthError)?.code })
        } catch { }
        return Promise.reject(reason)
      }
    },
    async sendVerificationEmail(redirectUrl?: string): Promise<void> {
      try {
        const auth = getAuth()
        const current = auth.currentUser
        if (!current) {
          logger.error('sendVerificationEmail: no current user')
          return
        }
        const url = redirectUrl || `${window.location.origin}/`
        await sendEmailVerification(current, { url, handleCodeInApp: true })
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('verification_email_sent')
        } catch { }
        try {
          useAppStore().toast('Verification email', 'Email sent', 'success', 4000)
        } catch { }
      } catch (error) {
        logger.error('Error sending verification email', error)
        const authError = error as AuthError
        const isRateLimited = authError?.code === 'auth/too-many-requests'

        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('verification_email_error', {
            message: (error as Error)?.message,
            code: authError?.code,
            isRateLimited
          })
        } catch { }

        try {
          if (isRateLimited) {
            useAppStore().toast(
              'Verification email',
              i18n.global.t('auth.verify.rate_limit_message'),
              'error',
              6000
            )
          } else {
            useAppStore().toast('Verification email', (error as Error)?.message || 'Failed to send', 'error', 5000)
          }
        } catch { }

        return Promise.reject(error)
      }
    },
    async reloadCurrentUser(): Promise<void> {
      try {
        const auth = getAuth()
        if (!auth.currentUser) return
        await reload(auth.currentUser)
        await this.setUser(auth.currentUser)
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('user_reload_success')
        } catch { }
      } catch (error) {
        logger.error('Error reloading current user', error)
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('user_reload_error', { message: (error as Error)?.message })
        } catch { }
        return Promise.reject(error)
      }
    },
    async passwordReset(email: string) {
      this.loading = true
      return sendPasswordResetEmail(getAuth(), email).then(() => {
        this.loading = false
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('password_reset_requested')
        } catch { }
      }).catch((reason) => {
        logger.error('Failed sendPasswordResetEmail', reason)
        this.passwordResetError = reason
        this.loading = false
        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('password_reset_failure', { code: (reason as AuthError)?.code })
        } catch { }
      });

    },
    /**
     * MYAC-1337 — store one answer the moment it is given, so leaving halfway keeps
     * what was said. The rule requires `answered_at` to be the server time. Resolves
     * false on failure: the chat moves on regardless — nothing here may block.
     */
    async saveOnboardingAnswer(question: OnboardingQuestion, answer: { text: string, choice: string | null }): Promise<boolean> {
      const uid = this.user?.uid
      const text = answer.text.trim().slice(0, ONBOARDING_TEXT_MAX)
      if (!uid || !text) return false
      try {
        const db = useFirestore()
        await updateDoc(doc(db, 'users', uid), {
          [`onboarding_answers.${question}`]: {
            text,
            choice: answer.choice,
            answered_at: serverTimestamp(),
          },
          updated_at: serverTimestamp(),
        })
        await this.getFirestoreUser(uid)
        return true
      } catch (error) {
        logger.error('Error saving onboarding answer', error)
        try {
          useAppStore().toast(i18n.global.t('common.error'), i18n.global.t('common.error_message'), 'error')
        } catch { }
        return false
      }
    },
    /**
     * MYAC-1522 — the person's name, from « Faisons connaissance » or the profile.
     * An empty field is stored as null (the rule refuses an empty string). The
     * display name always follows the two fields (« first last », either alone, or
     * null when both are empty — MYAC-1562): billing's users_updated trigger copies
     * it onto every member record of the person, so member lists, task names and
     * the support chat show the name the profile shows, or the email alone. Resolves
     * false (after a toast) when the write is refused.
     */
    // csm-event: none — a profile detail; CSM has no signal defined for it.
    async savePersonalName(name: { first_name: string, last_name: string }): Promise<boolean> {
      const uid = this.user?.uid
      if (!uid) return false
      const clean = (v: string) => v.trim().slice(0, PERSONAL_NAME_MAX) || null
      const first = clean(name.first_name)
      const last = clean(name.last_name)
      const payload: UpdateData<DocumentData> = {
        first_name: first,
        last_name: last,
        display_name: [first, last].filter(Boolean).join(' ') || null,
        updated_at: serverTimestamp(),
      }
      try {
        await updateDoc(doc(useFirestore(), 'users', uid), payload)
        await this.getFirestoreUser(uid)
        return true
      } catch (error) {
        logger.error('Error saving the personal name', error)
        try {
          useAppStore().toast(i18n.global.t('common.error'), i18n.global.t('common.error_message'), 'error')
        } catch { }
        return false
      }
    },
    /**
     * MYAC-1522 — « Passer cette question »: that step is never asked again, on any
     * surface. Resolves false on failure; the chat moves on regardless.
     */
    // csm-event: none — a skipped question is no product action.
    async skipOnboardingStep(step: OnboardingStep): Promise<boolean> {
      const uid = this.user?.uid
      if (!uid) return false
      try {
        await updateDoc(doc(useFirestore(), 'users', uid), {
          onboarding_skipped: arrayUnion(step),
          updated_at: serverTimestamp(),
        })
        await this.getFirestoreUser(uid)
        return true
      } catch (error) {
        logger.error('Error skipping an onboarding step', error)
        return false
      }
    },
    /** MYAC-1337 — the person put it off (« Plus tard »): not asked again until their next session. */
    skipOnboardingForSession() {
      const uid = this.user?.uid
      if (!uid) return
      this.onboardingSkippedUid = uid
      writeSessionSkip(uid)
    },
    /**
     * MYAC-1337 — erase every answer. The block goes entirely, and the questions
     * skipped for good are forgotten too (MYAC-1522), which puts the person back
     * among those never asked: the chat asks the three questions again. The name
     * stays: it is edited on the profile.
     */
    async eraseOnboardingAnswers(): Promise<boolean> {
      const uid = this.user?.uid
      if (!uid) return false
      try {
        const db = useFirestore()
        const payload: UpdateData<DocumentData> = { onboarding_answers: deleteField(), updated_at: serverTimestamp() }
        // Only when some question was skipped: an account that never skipped writes
        // exactly what it wrote before, so erasing works under the previous rule too.
        if (this.onboardingSkipped.some(step => step !== 'name')) {
          payload.onboarding_skipped = arrayRemove(...ONBOARDING_QUESTIONS)
        }
        await updateDoc(doc(db, 'users', uid), payload)
        this.onboardingSkippedUid = null
        clearSessionSkip(uid)
        await this.getFirestoreUser(uid)
        return true
      } catch (error) {
        logger.error('Error erasing onboarding answers', error)
        try {
          useAppStore().toast(i18n.global.t('common.error'), i18n.global.t('common.error_message'), 'error')
        } catch { }
        return false
      }
    },
    /**
     * MYAC-1522 — « Ne plus afficher à l'accueil » (from the letter) or back on
     * (from the profile). Kept on this browser at once, so the next arrival
     * follows it even before the account loads. Resolves false (after a toast)
     * when the write is refused.
     */
    // csm-event: none — a display preference; CSM has no signal defined for it.
    async updateWelcomeLetterHidden(hidden: boolean): Promise<boolean> {
      const uid = this.user?.uid
      if (!uid) return false
      writeWelcomeLetterHiddenHere(uid, hidden)
      try {
        await updateDoc(doc(useFirestore(), 'users', uid), {
          'notification_preferences.welcome_letter_hidden': hidden,
          updated_at: serverTimestamp(),
        })
        await this.getFirestoreUser(uid)
        return true
      } catch (error) {
        logger.error('Error updating the welcome letter preference', error)
        writeWelcomeLetterHiddenHere(uid, !hidden)
        try {
          useAppStore().toast(i18n.global.t('common.error'), i18n.global.t('common.error_message'), 'error')
        } catch { }
        return false
      }
    },
    /**
     * MYAC-1522 — save the interface language chosen in the profile (null = the
     * browser's) and switch the app to it at once. Resolves true once written;
     * false (after a toast) when the write is refused.
     */
    // csm-event: none — a display preference; CSM has no signal defined for it.
    async updateInterfaceLanguage(language: InterfaceLanguage | null): Promise<boolean> {
      const uid = this.user?.uid
      if (!uid) return false
      try {
        await updateDoc(doc(useFirestore(), 'users', uid), {
          'notification_preferences.interface_language': language,
          updated_at: serverTimestamp(),
        })
        setInterfaceLanguage(language)
        await this.getFirestoreUser(uid)
        return true
      } catch (error) {
        logger.error('Error saving the interface language', error)
        try {
          useAppStore().toast(i18n.global.t('common.error'), i18n.global.t('common.error_message'), 'error')
        } catch { }
        return false
      }
    },
    /** Resolves true once written; false when it could not be (already logged and toasted). */
    async updateNotificationPreferences(prefs: Partial<NotificationPreferences>): Promise<boolean> {
      if (!this.user?.uid) {
        logger.error('updateNotificationPreferences: no user logged in')
        return false
      }
      try {
        const db = useFirestore()
        const userRef = doc(db, 'users', this.user.uid)

        // Build update payload with dot-path notation
        const updatePayload: UpdateData<DocumentData> = {
          updated_at: serverTimestamp()
        }

        if (prefs.assessment_completed_projects !== undefined) {
          updatePayload['notification_preferences.assessment_completed_projects'] = prefs.assessment_completed_projects
        }
        if (prefs.regulation_watch_projects !== undefined) {
          updatePayload['notification_preferences.regulation_watch_projects'] = prefs.regulation_watch_projects
        }
        if (prefs.preferred_language !== undefined) {
          updatePayload['notification_preferences.preferred_language'] = prefs.preferred_language
        }
        if (prefs.notification_setup_completed !== undefined) {
          updatePayload['notification_preferences.notification_setup_completed'] = prefs.notification_setup_completed
        }
        if (prefs.weekly_summary_disabled !== undefined) {
          updatePayload['notification_preferences.weekly_summary_disabled'] = prefs.weekly_summary_disabled
        }
        if (prefs.watch_report_asked !== undefined) {
          updatePayload['notification_preferences.watch_report_asked'] = prefs.watch_report_asked
        }

        await updateDoc(userRef, updatePayload)

        // Refresh user data
        await this.getFirestoreUser(this.user.uid)

        try {
          const analytics = useAnalyticsStore()
          analytics.trackEvent('notification_preferences_updated')
        } catch { }
        try { trackAction(CsmAction.AccountNotificationsUpdated) } catch { }
        return true
      } catch (error) {
        logger.error('Error updating notification preferences', error)
        try {
          useAppStore().toast(
            i18n.global.t('common.error'),
            i18n.global.t('common.error_message'),
            'error'
          )
        } catch { }
        return false
      }
    },
  }
})
